import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { list_activity, useEventsRequest } from './events_api';
import {
  absoluteTime,
  actorName,
  buildLookup,
  describeActivity,
  relativeTime
} from './activity_format';

/**
 * Activity tab — the audit feed for a topic and everything under it.
 *
 * One row per changed field. The API returns newest-first with an opaque keyset cursor
 * echoed back as `before`, so this loads a page and offers "Load older" rather than
 * numbered pages: the log is append-only, and an offset would skip or repeat rows as
 * new entries land.
 *
 * Sort and filter are applied to the rows ALREADY LOADED, not pushed to the server.
 * That is a deliberate limitation and it is surfaced in the UI: reordering
 * oldest-first would otherwise imply you were seeing the oldest entries overall, when
 * you are seeing the oldest of the most recent page. A topic realistically has tens of
 * rows, so PAGE_SIZE is set to the server's maximum and one page is usually all of it.
 */

// The server caps `limit` at 200. Asking for the maximum keeps the client-side
// sort honest for any realistic topic.
const PAGE_SIZE = 200;

const ENTITY_FILTERS = [
  { label: 'All', value: 'all' },
  { label: 'Event', value: 'topic' },
  { label: 'Runs', value: 'run' },
  { label: 'Slots', value: 'slot' }
];

export default function ActivityTab({ topic }) {
  const [rows, setRows] = useState([]);
  const [newest_first, setNewestFirst] = useState(true);
  const [entity_filter, setEntityFilter] = useState('all');
  // The server's opaque next_cursor. It carries created_date AND id, because activity
  // rows are written in batches that share a created_date second and a timestamp-only
  // cursor silently skipped the rest of the batch. Empty means nothing older is left.
  const [cursor, setCursor] = useState('');
  const { run, loading } = useEventsRequest();

  // Resolves a row's entity_uuid to something readable from data the detail screen
  // has already loaded, so this needs no extra request.
  const lookup = useMemo(() => buildLookup(topic.runs), [topic.runs]);

  const load = useCallback(
    async (before) => {
      const { data } = await run(() =>
        list_activity(topic.topic_uuid, { limit: PAGE_SIZE, before })
      );
      if (!data) return;

      const page = data.data.activity || [];
      setRows((previous) => (before ? [...previous, ...page] : page));
      setCursor(data.data.next_cursor || '');
    },
    [run, topic.topic_uuid]
  );

  useEffect(() => {
    load();
  }, [load]);

  // Send back the server's cursor verbatim. Deriving one from the oldest row's
  // created_at is what dropped rows before, and the id it needs is not exposed.
  const load_older = () => {
    if (cursor) load(cursor);
  };

  const exhausted = cursor === '';

  const visible = useMemo(() => {
    const filtered =
      entity_filter === 'all' ? rows : rows.filter((row) => row.entity_type === entity_filter);
    // The server returns newest-first, so oldest-first reverses what is loaded
    // rather than issuing a different query.
    return newest_first ? filtered : [...filtered].reverse();
  }, [rows, entity_filter, newest_first]);

  return (
    <div
      className="rounded-lg"
      style={{
        background: 'var(--surface-card)',
        border: '1px solid var(--border-default)',
        boxShadow: 'var(--shadow-card)'
      }}
    >
      <div
        className="flex items-center gap-3 px-4 py-3"
        style={{ borderBottom: '1px solid var(--border-default)' }}
      >
        <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
          Activity
        </h3>

        <div className="ml-auto flex items-center gap-2">
          <select
            value={entity_filter}
            onChange={(e) => setEntityFilter(e.target.value)}
            aria-label="Filter activity"
            className="px-2 py-1 rounded text-sm"
            style={{
              background: 'var(--input-bg)',
              border: '1px solid var(--input-border)',
              color: 'var(--input-text)'
            }}
          >
            {ENTITY_FILTERS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <button
            type="button"
            className="ct-small-btn whitespace-nowrap"
            onClick={() => setNewestFirst((previous) => !previous)}
            title={
              newest_first ? 'Showing newest first' : 'Showing oldest first, of what is loaded'
            }
          >
            {newest_first ? '\u2193 Newest' : '\u2191 Oldest'}
          </button>
        </div>
      </div>

      {!loading && visible.length === 0 && (
        <div className="px-4 py-6 text-sm text-center" style={{ color: 'var(--text-muted)' }}>
          {rows.length === 0 ? 'No activity yet.' : 'Nothing matches this filter.'}
        </div>
      )}

      {visible.map((row) => (
        <div
          key={row.activity_uuid}
          className="flex items-start gap-3 px-4 py-3"
          style={{ borderTop: '1px solid var(--border-default)' }}
        >
          <span
            className="mt-1 rounded-full flex-shrink-0"
            style={{ width: 8, height: 8, background: 'var(--brand-primary)' }}
          />
          <div className="text-sm" style={{ color: 'var(--text-primary)' }}>
            <strong style={{ fontWeight: 500 }}>{actorName(row)}</strong>{' '}
            {describeActivity(row, lookup)}
          </div>
          <div
            className="ml-auto text-sm flex-shrink-0"
            style={{ color: 'var(--text-muted)' }}
            title={absoluteTime(row.created_at)}
          >
            {relativeTime(row.created_at)}
          </div>
        </div>
      ))}

      {rows.length > 0 && (
        <div
          className="px-4 py-3 flex items-center"
          style={{ borderTop: '1px solid var(--border-default)' }}
        >
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
            {/* Stated because the sort and filter above see only these rows. */}
            {rows.length} {rows.length === 1 ? 'entry' : 'entries'} loaded
            {exhausted ? ' — that is all of them' : ''}
          </span>
          {!exhausted && (
            <button
              type="button"
              className="ct-small-btn ml-auto"
              onClick={load_older}
              disabled={loading}
            >
              Load older
            </button>
          )}
        </div>
      )}
    </div>
  );
}

ActivityTab.propTypes = {
  topic: PropTypes.shape({
    topic_uuid: PropTypes.string,
    runs: PropTypes.arrayOf(PropTypes.shape({ run_uuid: PropTypes.string }))
  }).isRequired
};
